import { makeReminderApiEndpoints } from "./endpoints"
import { api } from "../../api"
import { ReminderEntityType, ReminderResponse, ReminderTriggerStatus } from "./reminder-response"
import { makeReminderViewModel, ReminderViewModel } from "./reminder"
import { makeUrlSearchParams } from "../../utils/url"
import { hasPendingMutations } from "../../utils/rtk-query-util"
import { calculateAbsoluteReminderTime } from "../utils/absolute-reminder-time"
import { DateTimeService } from "../../services/date-time-service"

const apiEndpoints = makeReminderApiEndpoints()

export const reminderApi = api.injectEndpoints({
  endpoints: (builder) => {
    return {
      getReminders: builder.query<ReminderViewModel[], GetRemindersQuery>({
        query: (params) => {
          return {
            method: "GET",
            url: apiEndpoints.reminders(),
            params: params ? makeUrlSearchParams(params) : undefined,
          }
        },
        transformResponse: (response: ReminderResponse[]) => response.map(makeReminderViewModel),
        providesTags: ["Reminder"],
      }),
      createReminder: builder.mutation<ReminderViewModel, NewReminderData>({
        query: (reminderData) => {
          return {
            method: "POST",
            url: apiEndpoints.reminders(),
            body: reminderData,
          }
        },
        invalidatesTags: ["Reminder"],
      }),
      updateReminderTrigger: builder.mutation<ReminderViewModel, { reminderId: string } & ReminderTriggerUpdateData>({
        query: ({ reminderId, ...updateData }) => {
          return {
            method: "PUT",
            url: apiEndpoints.reminderTrigger(reminderId),
            body: updateData,
          }
        },
        async onQueryStarted({ reminderId, ...updateData }, { dispatch, getState, queryFulfilled, ...rest }) {
          const state = getState() as any
          // find the getReminders query params to optimistically update
          const reminderQueries = state.api?.queries ?? state.reminderApi?.queries ?? {}
          const reminderKey = Object.keys(reminderQueries).find((key) =>
            reminderQueries[key]?.endpointName.includes("getReminders")
          )
          const entry = reminderQueries[reminderKey ?? ""]
          if (entry?.endpointName !== "getReminders" || !entry?.originalArgs) {
            return
          }
          // optimistic update
          const patchResult = dispatch(
            reminderApi.util.updateQueryData("getReminders", entry.originalArgs, (draft) => {
              const reminder = draft.find((r) => r.id === reminderId)
              if (!reminder) return
              reminder.triggerType = updateData.triggerType

              // reset all trigger details
              reminder.isAbsoluteTimeReminder = false
              reminder.isRelativeTimeReminder = false
              reminder.isStatusChangeReminder = false
              if (updateData.triggerType === "absoluteTime") {
                reminder.reminderTime = new Date(updateData.reminderTime)
                reminder.reminderTimeDetails = null
                reminder.isAbsoluteTimeReminder = true
                reminder.isExpired = DateTimeService.isExpiredTodayOrBefore(reminder.reminderTime)
              }
              if (updateData.triggerType === "relativeTime") {
                reminder.reminderTimeDetails = updateData.reminderTimeDetails
                reminder.isRelativeTimeReminder = true
                if (updateData.reminderTimeDetails) {
                  const absoluteTime = calculateAbsoluteReminderTime(updateData.reminderTimeDetails)
                  reminder.reminderTime = absoluteTime
                  reminder.isExpired = DateTimeService.isExpiredTodayOrBefore(absoluteTime)
                }
              }
              if (updateData.triggerType === "statusChange") {
                reminder.triggerStatus = updateData.triggerStatus
                reminder.isStatusChangeReminder = true
              }
            })
          )
          try {
            await queryFulfilled
            const state = getState()
            if (
              !hasPendingMutations(state, {
                endpointNames: "updateReminderTrigger",
              })
            ) {
              dispatch(reminderApi.util.invalidateTags(["Reminder"]))
            }
          } catch {
            patchResult.undo()
          }
        },
      }),
      updateReminderRecipients: builder.mutation<
        ReminderViewModel,
        { reminderId: string } & ReminderRecipientsUpdateData
      >({
        query: ({ reminderId, ...updateData }) => {
          return {
            method: "PUT",
            url: apiEndpoints.reminderRecipients(reminderId),
            body: updateData,
          }
        },
        invalidatesTags: ["Reminder"],
      }),
      updateNote: builder.mutation<ReminderViewModel, { reminderId: string } & ReminderNoteUpdateData>({
        query: ({ reminderId, note }) => {
          return {
            method: "PUT",
            url: apiEndpoints.reminderNote(reminderId),
            body: { note },
          }
        },
        invalidatesTags: ["Reminder"],
      }),
      deleteReminder: builder.mutation<void, string>({
        query: (reminderId) => {
          return {
            method: "DELETE",
            url: apiEndpoints.reminderItem(reminderId),
          }
        },
        invalidatesTags: ["Reminder"],
      }),
    }
  },
})

export const {
  useGetRemindersQuery,
  useCreateReminderMutation,
  useUpdateReminderRecipientsMutation,
  useUpdateReminderTriggerMutation,
  useUpdateNoteMutation,
  useDeleteReminderMutation,
} = reminderApi

export type BaseNewReminderData = {
  entityType: ReminderEntityType
  entityId: string
  triggerType: "absoluteTime" | "relativeTime" | "statusChange"
  recipients: {
    roles: ReminderResponse["recipients"]["roles"]
    emails: string[]
  }
}
type NewRelativeReminderData = {
  reminderTimeDetails: ReminderResponse["reminderTimeDetails"]
  reminderTime?: undefined
}
type NewAbsoluteReminderData = { reminderTime: string; reminderTimeDetails?: undefined }
type AbsoluteReminderTimeUpdateData = { reminderTime: string }
type RelativeReminderTimeUpdateData = { reminderTimeDetails: ReminderResponse["reminderTimeDetails"] }
type NewStatusReminderData = { triggerStatus: ReminderTriggerStatus }

export type NewReminderData = BaseNewReminderData &
  (NewRelativeReminderData | NewAbsoluteReminderData | NewStatusReminderData)
export type ReminderTimeUpdateData = AbsoluteReminderTimeUpdateData | RelativeReminderTimeUpdateData
export type ReminderRecipientsUpdateData = Partial<ReminderResponse["recipients"]>
export type DeleteAllExpiredRemindersQuery = { project: string }
export type ReminderNoteUpdateData = { note: string | null }

type AbsoluteReminderUpdateData = {
  triggerType: "absoluteTime"
  reminderTime: string
}

type RelativeReminderUpdateData = {
  triggerType: "relativeTime"
  reminderTimeDetails: ReminderResponse["reminderTimeDetails"]
}

type StatusChangeReminderUpdateData = {
  triggerType: "statusChange"
  triggerStatus: ReminderTriggerStatus
  triggerSubStatusId?: string | null
}

type GetRemindersQuery = { project: string } | { task: string } | { calendarEvent: string } | { todo: string }

export type ReminderTriggerUpdateData =
  | AbsoluteReminderUpdateData
  | RelativeReminderUpdateData
  | StatusChangeReminderUpdateData
